import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

const HealthStoryFlow = () => {
  const navigate = useNavigate();
  
  const [phase, setPhase] = useState('past'); // 'past', 'payment', 'future'
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isNarrating, setIsNarrating] = useState(false);
  const [showNarration, setShowNarration] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [selectedYear, setSelectedYear] = useState(0);
  const [pastStory, setPastStory] = useState(null);
  const [futureStory, setFutureStory] = useState(null);
  
  const [yearlyPredictions, setYearlyPredictions] = useState([]);
  const [narrationText, setNarrationText] = useState('');
  
  const audioRef = useRef(null);
  const narrationIntervalRef = useRef(null);

  const storedData = localStorage.getItem('birth_details');
  const birthData = storedData ? JSON.parse(storedData) : {
    name: "Demo User",
    dateOfBirth: "14-05-1990",
    timeOfBirth: "10:30 AM",
    placeOfBirth: "Delhi, India",
    latitude: 28.6139,
    longitude: 77.2090,
    gender: "Male"
  };

  const parseBirthData = () => {
    const [day, month, year] = birthData.dateOfBirth.split('-').map(Number);
    const timeParts = birthData.timeOfBirth.match(/(\d+):(\d+)\s*(AM|PM)/i);
    let hour = parseInt(timeParts[1]);
    const minute = parseInt(timeParts[2]);
    const period = timeParts[3].toUpperCase();
    
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;

    return {
      name: birthData.name,
      year, month, day, hour, minute,
      latitude: birthData.latitude || 28.6139,
      longitude: birthData.longitude || 77.2090,
      gender: birthData.gender?.toLowerCase() || 'male'
    };
  };

  const PLANET_ICONS = {
    'Sun': '☉', 'Moon': '☽', 'Mars': '♂', 'Mercury': '☿',
    'Jupiter': '♃', 'Venus': '♀', 'Saturn': '♄', 'Rahu': '☊', 'Ketu': '☋'
  };

  useEffect(() => {
    loadStoryData();
  }, [phase]);

  const loadStoryData = async () => {
    if (phase === 'payment') return;
    
    setIsLoading(true);
    setLoadingProgress(0);
    
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) { clearInterval(progressInterval); return prev; }
        return prev + 10;
      });
    }, 150);

    try {
      const apiData = parseBirthData();
      const endpoint = phase === 'past' ? '/api/health/story/past' : '/api/health/story/future';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiData)
      });

      const result = await response.json();
      
      if (result.success) {
        if (phase === 'past') {
          setPastStory(result.data);
          const timeline = (result.data.timeline_events || []).map((event) => {
            const planet = event.event?.split(' ')[0] || 'Venus';
            return {
              year: event.year,
              age: event.age?.replace('Age ', '') || '',
              planet: planet,
              zodiac: PLANET_ICONS[planet] || '●',
              event: event.event,
              detail: event.detail
            };
          });
          setYearlyPredictions(timeline.length > 0 ? timeline : generateDefaultTimeline('past'));
          setNarrationText(result.data.narration_script || '');
        } else {
          setFutureStory(result.data);
          const timeline = (result.data.future_timeline || []).map((event) => {
            const planet = event.dasha_lord || 'Mars';
            return {
              year: event.years_range?.split('-')[0] || event.year,
              age: event.age_range || '',
              planet: planet,
              zodiac: PLANET_ICONS[planet] || '●',
              event: event.health_type,
              detail: event.affected_organs?.join(', '),
              risk_level: event.risk_level,
              is_current: event.is_current
            };
          });
          setYearlyPredictions(timeline.length > 0 ? timeline : generateDefaultTimeline('future'));
          setNarrationText(result.data.narration_script || '');
        }
        
        setLoadingProgress(100);
        setTimeout(() => setIsLoading(false), 500);
      }
    } catch (err) {
      console.error('Error loading story:', err);
      setYearlyPredictions(generateDefaultTimeline(phase));
      setIsLoading(false);
    }
  };

  const generateDefaultTimeline = (type) => {
    if (type === 'past') {
      return [
        { year: '1997', planet: 'Venus', zodiac: '♀' },
        { year: '2002', planet: 'Sun', zodiac: '☉' },
        { year: '2012', planet: 'Moon', zodiac: '☽' },
        { year: '2015', planet: 'Mars', zodiac: '♂' },
        { year: '2016', planet: 'Rahu', zodiac: '☊' },
        { year: '2025', planet: 'Jupiter', zodiac: '♃' },
        { year: '2026', planet: 'Venus', zodiac: '♀' },
        { year: '2027', planet: 'Mercury', zodiac: '☿' },
        { year: '2028', planet: 'Venus', zodiac: '♀' },
        { year: '2030+', planet: 'Moon', zodiac: '☽' },
      ];
    } else {
      return [
        { year: '2025', planet: 'Jupiter', zodiac: '♃' },
        { year: '2026', planet: 'Venus', zodiac: '♀' },
        { year: '2027', planet: 'Mercury', zodiac: '☿' },
        { year: '2028', planet: 'Venus', zodiac: '♀' },
        { year: '2029', planet: 'Moon', zodiac: '☽' },
        { year: '2030+', planet: 'Saturn', zodiac: '♄' },
      ];
    }
  };

  const startNarration = async () => {
    if (isNarrating) { stopNarration(); return; }

    setIsNarrating(true);
    setShowNarration(true);
    setSelectedYear(0);

    let yearIdx = 0;
    narrationIntervalRef.current = setInterval(() => {
      yearIdx++;
      if (yearIdx < yearlyPredictions.length) {
        setSelectedYear(yearIdx);
      } else {
        clearInterval(narrationIntervalRef.current);
        setTimeout(() => {
          setIsNarrating(false);
          if (phase === 'past') {
            toast.success('Story complete! Unlock your future predictions.');
          }
        }, 2000);
      }
    }, 4000);

    if (narrationText) {
      try {
        const response = await fetch(`${API_URL}/api/tts/edge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: narrationText, language: selectedLanguage })
        });

        const result = await response.json();
        
        if (result.success && result.audio) {
          const audioBlob = new Blob(
            [Uint8Array.from(atob(result.audio), c => c.charCodeAt(0))],
            { type: 'audio/mp3' }
          );
          const audioUrl = URL.createObjectURL(audioBlob);
          
          if (audioRef.current) {
            audioRef.current.src = audioUrl;
            await audioRef.current.play();
          }
        }
      } catch (err) {
        console.error('TTS error:', err);
      }
    }
  };

  const stopNarration = () => {
    setIsNarrating(false);
    setShowNarration(false);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    if (narrationIntervalRef.current) { clearInterval(narrationIntervalRef.current); }
  };

  const handlePayment = () => {
    toast.success('🎉 Payment successful! Unlocking future predictions...');
    setTimeout(() => setPhase('future'), 1500);
  };

  const currentYear = yearlyPredictions[selectedYear] || {};

  // Loading Screen
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #0a1628 0%, #020712 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          position: 'fixed', inset: 0,
          background: `radial-gradient(2px 2px at 20% 30%, white, transparent)`,
          backgroundSize: '100vw 100vh', opacity: 0.4, pointerEvents: 'none'
        }} />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center', zIndex: 1 }}>
          <p style={{ fontSize: '18px', color: '#edc161', marginBottom: '20px', fontWeight: 500 }}>
            Preparing your {phase === 'past' ? 'Health Story' : 'Future Predictions'}...
          </p>
          <div style={{ width: '200px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden', margin: '0 auto' }}>
            <motion.div style={{ width: `${loadingProgress}%`, height: '100%', background: 'linear-gradient(90deg, #edc161, #edc16180)', borderRadius: '2px' }} />
          </div>
        </motion.div>
      </div>
    );
  }

  // Payment Gate
  if (phase === 'payment') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #0a1628 0%, #020712 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}>
        <div style={{ position: 'fixed', inset: 0, background: `radial-gradient(2px 2px at 20% 30%, white, transparent)`, backgroundSize: '100vw 100vh', opacity: 0.4, pointerEvents: 'none' }} />
        
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{
          background: 'linear-gradient(135deg, rgba(237, 193, 97, 0.15), rgba(255, 153, 51, 0.1))',
          borderRadius: '24px', padding: '40px 30px', maxWidth: '380px', textAlign: 'center',
          border: '2px solid rgba(237, 193, 97, 0.3)', position: 'relative', zIndex: 1
        }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔮</div>
          <h2 style={{ color: '#edc161', fontSize: '24px', marginBottom: '15px' }}>Unlock Your Future</h2>
          <p style={{ color: '#E8E8E8', fontSize: '14px', marginBottom: '25px' }}>Get detailed future health predictions</p>
          <div style={{ textAlign: 'left', marginBottom: '25px' }}>
            {['Future health timeline', 'Surgery risk windows', 'Longevity analysis', 'Personalized recommendations'].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', color: '#E8E8E8', fontSize: '14px' }}>
                <span style={{ color: '#22c55e' }}>✓</span>{item}
              </div>
            ))}
          </div>
          <div style={{ padding: '15px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', marginBottom: '20px' }}>
            <span style={{ fontSize: '32px', fontWeight: 800, color: '#edc161' }}>₹99</span>
            <span style={{ color: '#9CA3AF', fontSize: '14px', marginLeft: '8px' }}>one-time</span>
          </div>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handlePayment}
            style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '16px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            <Unlock size={20} />Pay & Unlock Now
          </motion.button>
          <button onClick={() => setPhase('past')} style={{ marginTop: '15px', background: 'transparent', border: 'none', color: '#9CA3AF', fontSize: '14px', cursor: 'pointer' }}>← Back to Past Story</button>
        </motion.div>
      </div>
    );
  }

  // Main Story View - MATCHING EXACT DESIGN
  return (
    <div style={{
      minHeight: '100vh', height: '100vh',
      background: 'radial-gradient(ellipse at center, #0a1628 0%, #020712 100%)',
      position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column'
    }}>
      <audio ref={audioRef} onEnded={stopNarration} />
      
      {/* Stars Background */}
      <div style={{
        position: 'fixed', inset: 0,
        background: `radial-gradient(2px 2px at 20% 30%, white, transparent), radial-gradient(2px 2px at 60% 70%, white, transparent), radial-gradient(1px 1px at 50% 50%, white, transparent)`,
        backgroundSize: '100vw 100vh', opacity: 0.4, pointerEvents: 'none'
      }} />

      {/* Language Pills - Top Center */}
      <div style={{
        position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: '0', zIndex: 100, background: 'rgba(10, 22, 40, 0.6)',
        backdropFilter: 'blur(15px)', borderRadius: '25px', border: '1.5px solid rgba(237, 193, 97, 0.25)', overflow: 'hidden'
      }}>
        {[
          { key: 'hindi', label: 'हिंदी' },
          { key: 'hinglish', label: 'Hinglish' },
          { key: 'english', label: 'English' }
        ].map((lang) => (
          <button key={lang.key} onClick={() => setSelectedLanguage(lang.key)}
            style={{
              padding: '10px 20px', border: 'none',
              background: selectedLanguage === lang.key ? 'rgba(237, 193, 97, 0.3)' : 'transparent',
              color: selectedLanguage === lang.key ? '#edc161' : '#9CA3AF',
              fontSize: '14px', fontWeight: selectedLanguage === lang.key ? 700 : 500, cursor: 'pointer'
            }}>
            {lang.label}
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '80px 20px 20px', position: 'relative', zIndex: 1
      }}>
        {/* Sage Avatar */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          onClick={!isNarrating ? startNarration : undefined}
          style={{ cursor: !isNarrating ? 'pointer' : 'default', position: 'relative' }}
        >
          <img 
            src="/sage-final.png" 
            alt="Sage"
            style={{
              width: 'clamp(180px, 40vw, 280px)', height: 'auto',
              opacity: isNarrating ? 0.95 : 0.8,
              filter: isNarrating ? 'drop-shadow(0 0 40px rgba(237, 193, 97, 0.7))' : 'drop-shadow(0 0 20px rgba(237, 193, 97, 0.4))',
              transition: 'all 0.6s ease'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          
          {/* Skip Story Button - Near Sage */}
          {!isNarrating && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={(e) => { e.stopPropagation(); phase === 'past' ? setPhase('payment') : navigate('/home'); }}
              style={{
                position: 'absolute', top: '50%', right: '-120px', transform: 'translateY(-50%)',
                background: 'rgba(10, 22, 40, 0.8)', backdropFilter: 'blur(10px)',
                border: '1px solid rgba(237, 193, 97, 0.3)', borderRadius: '20px',
                padding: '10px 20px', color: '#9CA3AF', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              Skip Story <span style={{ color: '#edc161' }}>→</span>
            </motion.button>
          )}
        </motion.div>

        {/* Tap to Start */}
        {!isNarrating && (
          <motion.div
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              textAlign: 'center', color: '#edc161', fontSize: 'clamp(16px, 3.5vw, 20px)',
              marginTop: '20px', fontWeight: 600
            }}
          >
            ✨ Tap to Start Your Story ✨
          </motion.div>
        )}

        {/* Narration Box - Shows during narration */}
        <AnimatePresence>
          {showNarration && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                background: 'linear-gradient(135deg, rgba(237, 193, 97, 0.15), rgba(237, 193, 97, 0.05))',
                backdropFilter: 'blur(10px)', border: '1.5px solid rgba(237, 193, 97, 0.4)',
                borderRadius: '16px', padding: '20px 30px', marginTop: '20px',
                textAlign: 'center', maxWidth: '450px'
              }}
            >
              <div style={{ fontSize: 'clamp(20px, 4vw, 28px)', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                Year {currentYear.year}
              </div>
              <div style={{ fontSize: 'clamp(14px, 3vw, 16px)', color: '#ffffff90', marginBottom: '10px' }}>
                Age {currentYear.age} • {currentYear.year}
              </div>
              {currentYear.event && (
                <div style={{ fontSize: 'clamp(13px, 2.8vw, 15px)', color: '#edc161', fontWeight: 600 }}>
                  {currentYear.zodiac} {currentYear.planet} - {currentYear.event}
                </div>
              )}
              {currentYear.risk_level && (
                <span style={{
                  display: 'inline-block', marginTop: '10px', padding: '5px 14px', borderRadius: '10px',
                  background: currentYear.risk_level === 'HIGH' ? 'rgba(239, 68, 68, 0.2)' : currentYear.risk_level === 'MODERATE' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                  color: currentYear.risk_level === 'HIGH' ? '#ef4444' : currentYear.risk_level === 'MODERATE' ? '#fbbf24' : '#22c55e',
                  fontSize: '12px', fontWeight: 700
                }}>
                  {currentYear.risk_level} RISK
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* TIMELINE - NO BOX, just dots and line */}
      <div style={{
        position: 'absolute', bottom: '80px', left: '5%', right: '5%', zIndex: 10
      }}>
        {/* Timeline Line */}
        <div style={{
          position: 'absolute', left: '0', right: '0', top: '50%', height: '3px',
          background: 'rgba(237, 193, 97, 0.4)', borderRadius: '3px', transform: 'translateY(-50%)'
        }} />

        {/* Progress Line */}
        {isNarrating && (
          <motion.div
            animate={{ scaleX: (selectedYear + 1) / yearlyPredictions.length }}
            style={{
              position: 'absolute', left: '0', right: '0', top: '50%', height: '4px',
              background: 'linear-gradient(90deg, #edc161, #edc161)',
              transformOrigin: 'left', zIndex: 5, boxShadow: '0 0 15px #edc161',
              borderRadius: '3px', transform: 'translateY(-50%)'
            }}
          />
        )}

        {/* Timeline Dots */}
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 30 }}>
          {yearlyPredictions.map((year, idx) => {
            const isActive = isNarrating && selectedYear === idx;
            return (
              <motion.div key={idx} onClick={() => !isNarrating && setSelectedYear(idx)}
                animate={{ scale: isActive ? 1.5 : 1 }}
                style={{ cursor: !isNarrating ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: isActive ? 50 : 10 }}>
                
                {/* Golden Dot */}
                <motion.div
                  animate={{ width: isActive ? '50px' : '14px', height: isActive ? '50px' : '14px' }}
                  transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                  style={{
                    borderRadius: '50%',
                    background: isActive ? 'radial-gradient(circle at 30% 30%, #edc16160, #edc16120)' : '#edc161',
                    border: isActive ? '2px solid #edc161' : '2px solid #edc161',
                    boxShadow: isActive ? '0 0 30px #edc161CC, 0 0 60px #edc16150' : '0 0 8px #edc16140',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: isActive ? '20px' : '0px', fontWeight: 800, color: '#FFFFFF'
                  }}
                >
                  {isActive && year.zodiac}
                </motion.div>

                {/* Year Label */}
                <motion.span
                  animate={{ opacity: 1, scale: isActive ? 1.2 : 1 }}
                  style={{
                    marginTop: isActive ? '10px' : '8px',
                    fontSize: isActive ? '14px' : '11px',
                    fontWeight: isActive ? 800 : 600,
                    color: isActive ? '#FFFFFF' : '#edc161',
                    textShadow: isActive ? '0 0 10px #edc161' : 'none'
                  }}
                >
                  {year.year}
                </motion.span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bottom Action */}
      {!isNarrating && phase === 'past' && (
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setPhase('payment')}
          style={{
            position: 'absolute', bottom: '20px', left: '20px', right: '20px',
            padding: '16px', background: 'linear-gradient(135deg, #edc161, #d4a84b)', border: 'none',
            borderRadius: '14px', color: '#0a1628', fontSize: '16px', fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            zIndex: 100
          }}>
          <Lock size={20} />Unlock Future Predictions
        </motion.button>
      )}

      {phase === 'future' && !isNarrating && futureStory?.longevity && (
        <div style={{
          position: 'absolute', bottom: '20px', left: '20px', right: '20px',
          padding: '14px', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1))',
          borderRadius: '14px', border: '1px solid rgba(139, 92, 246, 0.4)', textAlign: 'center', zIndex: 100
        }}>
          <p style={{ color: '#a78bfa', fontSize: '12px', marginBottom: '4px' }}>⏳ Longevity Analysis</p>
          <p style={{ color: '#E8E8E8', fontSize: '15px', fontWeight: 600 }}>{futureStory.longevity.class}</p>
        </div>
      )}
    </div>
  );
};

export default HealthStoryFlow;
